type ViewObserverOptions = IntersectionObserverInit & {
  trackVisibility?: boolean;
  delay?: number;
};

type IntersectionObserverEntryV2 = IntersectionObserverEntry & { isVisible?: boolean };

type ElementDataOptions = {
  /**
   * Callback function that will be called when the element appears in the viewport or disappears from it
   */
  callback?: (element: Element, isInView: boolean, entry: IntersectionObserverEntryV2) => void | Promise<void>;

  /**
   * If the element was visible once and the callback was called, the element will not be observed anymore
   */
  freezeOnceVisible?: boolean;

  /**
   * Track all changes emitted by the IntersectionObserver
   */
  trackAllChanges?: boolean;

  /**
   * Minimum time the element should be visible before the callback is called
   */
  minimumVisibleTime?: number;

  /**
   * Threshold representing the amount of the element that should be visible before the callback is called
   */
  threshold?: number;
}

export class ViewObserver {
  private readonly elementMap = new Map<Element, ElementDataOptions>();
  private readonly timeoutsMap = new Map<Element, ReturnType<typeof setTimeout>>();
  private readonly observer: IntersectionObserver;
  private readonly mutationObserver: MutationObserver;

  private static ELEMENT_DEFAULT_OPTIONS: ElementDataOptions = {
    freezeOnceVisible: false,
    threshold: 0.25,
    trackAllChanges: false,
  };

  private static DEFAULT_OPTIONS: ViewObserverOptions = {
    root: null,
    rootMargin: '0px',
    threshold: [ ...Array(100).fill(null).map((_, i) => i/100), 1 ],
  };

  private constructor (private readonly options?: ViewObserverOptions) {
    this.options = {
      ...ViewObserver.DEFAULT_OPTIONS,
      ...options,
    };

    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), this.options);
    this.mutationObserver = new MutationObserver(this.handleMutations.bind(this));
  }

  public observe (element: Element, options: ElementDataOptions): void {
    if (!element) {
      return;
    }

    if (this.elementMap.has(element)) {
      this.elementMap.delete(element);
      this.observer.unobserve(element);
    }

    this.elementMap.set(element, {
      ...ViewObserver.ELEMENT_DEFAULT_OPTIONS,
      ...options,
    });

    this.observer.observe(element);
    const parentElement = element.parentElement;

    if (parentElement) {
      this.mutationObserver.observe(parentElement, { childList: true, subtree: false });
    }
  }

  public unobserve (element: Element): void {
    if (!element) {
      return;
    }

    this.elementMap.delete(element);
    this.timeoutsMap.delete(element);
    this.observer.unobserve(element);

    if (this.elementMap.size === 0) {
      this.destroy();
    }
  }

  public destroy (): void {
    this.elementMap.clear();
    this.timeoutsMap.clear();

    this.observer.disconnect();
    this.mutationObserver.disconnect();
  }

  public static getInstance (options?: ViewObserverOptions): ViewObserver {
    return new ViewObserver(options);
  }

  private handleIntersection (entries: IntersectionObserverEntry[]): void {
    entries
      .filter((entry) => this.elementMap.has(entry.target))
      .forEach((entry: IntersectionObserverEntryV2): void => {
        const element = entry.target;
        const elementOptions = this.elementMap.get(element);

        if (!elementOptions) {
          return;
        }

        const elementThreshold = elementOptions.threshold as number;
        const trackAllChanges = elementOptions.trackAllChanges;
        const trackVisibility = this.options?.trackVisibility;
        const isIntersecting = entry.isIntersecting && entry.intersectionRatio >= elementThreshold;

        let isInView = isIntersecting;

        if (trackVisibility && typeof entry.isVisible !== 'undefined') {
          isInView = entry.isVisible && isIntersecting;
        }

        const attributeName = 'data-isInView';
        const isInViewStateChanged = !element.hasAttribute(attributeName)
          || (element.hasAttribute(attributeName) && element.getAttribute(attributeName) !== String(isInView));

        if (trackAllChanges || (!trackAllChanges && isInViewStateChanged)) {
          this.onVisibilityChange(entry.target, { entry, isInView });
          element.setAttribute(attributeName, String(isInView));
        }
      });
  }

  private onVisibilityChange (element: Element, data: { entry: IntersectionObserverEntryV2, isInView: boolean }): void {
    const options = this.elementMap.get(element);

    if (!options) {
      return;
    }

    const { entry, isInView } = data;

    const callback = options.callback ?? ((): void => void 0);

    if (isInView) {
      if (this.timeoutsMap.has(element)) {
        return;
      }

      this.timeoutsMap.set(element, setTimeout((): void => {
        Promise.resolve(callback(element, isInView, entry)).then((): void => void 0);

        if (options.freezeOnceVisible) {
          this.observer.unobserve(element);
        }

        this.timeoutsMap.delete(element);
      }, options.minimumVisibleTime ?? 0));

      return;
    }

    const timeoutId = this.timeoutsMap.get(element);
    clearTimeout(timeoutId);
    this.timeoutsMap.delete(element);
    Promise.resolve(callback(element, isInView, entry)).then((): void => void 0);
  }

  private handleMutations (mutations: MutationRecord[]): void {
    mutations.forEach((mutation: MutationRecord): void => {
      if (mutation.removedNodes.length) {
        mutation.removedNodes.forEach((node: Node): void => {
          if (node instanceof Element && this.elementMap.has(node)) {
            this.unobserve(node);
          }
        });
      }
    });
  }
}
